import { MapPin, Truck, Info, Settings } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useLanguage } from "@/context/LanguageContext";
import { ordersLabels, getCountryDisplayName } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";

interface Address {
  name?: string;
  street: string;
  postalCode: string;
  city: string;
  country: string;
}

interface AddressDisplayProps {
  type: "pickup" | "delivery";
  address?: Address;
}

export const AddressDisplay = ({ type, address }: AddressDisplayProps) => {
  const { language } = useLanguage();
  const db = ordersLabels(language).deliveryBooking;
  const Icon = type === "pickup" ? MapPin : Truck;
  const title = type === "pickup" ? db.pickupAddress.replace(":", "") : db.deliveryAddress.replace(":", "");
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const navigate = useNavigate();

  return (
    <div className="flex items-start gap-3">
      <Icon className="w-5 h-5 text-accent mt-1" />
      <div className={`flex-1 ${isDark ? "text-white/80" : "text-black/80"}`}>
        <div className="flex items-center gap-2">
          <div className="font-semibold">{title}</div>
          {type === "delivery" && (
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-blue-500 hover:text-blue-600"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Info className="h-4 w-4" />
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">{db.deliveryAddress.replace(":", "")}</h3>
                  <p className="text-sm">
                    {db.deliveryAddressInfo}
                  </p>
                  <Button
                    onClick={() => navigate("/settings?tab=profile")}
                    className="w-full"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    {db.goToSettings}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
        {address && address.street && address.postalCode && address.city ? (
          <>
            {address.name && <div>{address.name}</div>}
            <div>{address.street}</div>
            <div>{address.postalCode}</div>
            {address.country && (
              <div>
                {address.city}, {getCountryDisplayName(address.country, language)}
              </div>
            )}
          </>
        ) : (
          <div className="text-accent">
            {db.fillDeliveryAddress}
          </div>
        )}
      </div>
    </div>
  );
};
